import { setPageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClientWall } from "@/components/wve/ClientWall";
import { PageCTA } from "@/components/wve/PageCTA";

const categories = ["All", "Business Content & Promo", "Trades", "Live & Events", "Dance & Theatre", "Video Production"];

const works = [
  { id: "KdZedXdzy-E", title: "Plan It — Promo Video", cat: "Business Content & Promo" },
  { id: "b3bRIuba3Zk", title: "Talking Head & Vox Pop Examples", cat: "Business Content & Promo" },
  { id: "ykASclNX5aI", title: "HJJ Developments — House Renovation Project", cat: "Trades" },
  { id: "PwZ5G_gs42U", title: "How to Identify Asbestos — Asbestos Environmental Removals", cat: "Trades" },
  { id: "BCm65EtKLwE", title: "Event Live Stream", cat: "Live & Events" },
  { id: "re8BmH8SiC4", title: "Curtains Up 2025 — Highlights", cat: "Dance & Theatre" },
  { id: "Eb85p3YORSc", title: "Multi Camera Videography", cat: "Video Production" },
  { id: "6XnnCPyqflg", title: "Baby Ballers Franchise — Promo", cat: "Business Content & Promo" },
];

const WorkCard = ({ w, index }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.07 }}
      className="group"
      data-testid={`work-card-${w.id}`}
    >
      <div className="relative aspect-video rounded-lg overflow-hidden bg-[#0A0A0A]">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${w.id}?autoplay=1&rel=0`}
            title={w.title}
            className="w-full h-full border-0"
            allow="autoplay; fullscreen; encrypted-media"
          />
        ) : (
          <button onClick={() => setPlaying(true)} data-testid={`work-play-${w.id}`} className="w-full h-full block">
            <img loading="lazy"
              src={`https://img.youtube.com/vi/${w.id}/hqdefault.jpg`}
              alt={w.title}
              className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-[#E80042] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-white ml-1 inline-block" />
              </span>
            </span>
            <span className="absolute top-4 left-4 bg-black/70 backdrop-blur text-white text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full">
              {w.cat}
            </span>
          </button>
        )}
      </div>
      <h3 className="font-grotesk font-bold text-lg mt-4 leading-snug group-hover:text-[#E80042] transition-colors duration-200">{w.title}</h3>
    </motion.div>
  );
};

export default function WorkPage() {
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? works : works.filter((w) => w.cat === cat);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Our Work — Video Portfolio | WVE Studios",
      "Watch WVE Studios' latest work: business promos, live event streams, dance show films, trades videos and our showreel — shot across Essex and the UK.",
      "/work"
    );
    let ld = document.getElementById("video-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "video-jsonld";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: works.map((w, i) => ({
        "@type": "VideoObject",
        position: i + 1,
        name: w.title,
        description: `${w.cat} film by WVE Studios, Essex.`,
        thumbnailUrl: `https://img.youtube.com/vi/${w.id}/hqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${w.id}`,
        uploadDate: "2025-01-01",
      })),
    });
    return () => document.getElementById("video-jsonld")?.remove();
  }, []);

  return (
    <main data-testid="work-page">
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <span className="absolute -right-16 top-0 font-display text-[20rem] leading-none text-white/[0.05] select-none hidden lg:block">WORK</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">Our Work</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              Stuff we're<br /><span className="text-[#E80042]">proud of.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed">
              Promos, live streams, dance shows, trades and everything in-between — a taste of what leaves the edit suite.
            </p>
            <Link to="/services/artist-showreels" data-testid="work-showreels-link" className="inline-block mt-7 link-underline font-grotesk font-bold text-lg text-white">
              + 10 more on the Artist Showreels page →
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7"
            data-testid="work-featured-video"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E80042] mb-3">▶ The Showreel</p>
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src="https://www.youtube.com/embed/GaeoGkGGC9o?rel=0"
                title="WVE Studios 2025 Showreel"
                className="w-full h-full border-0"
                allow="autoplay; fullscreen; encrypted-media"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="work-grid-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`work-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-sm uppercase tracking-[0.12em] font-semibold border-2 transition-colors duration-200 ${
                  cat === c ? "bg-[#E80042] border-[#E80042] text-white" : "border-black/15 text-[#0A0A0A] hover:border-[#E80042] hover:text-[#E80042]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {filtered.map((w, i) => (
              <WorkCard key={w.id} w={w} index={i} />
            ))}
          </div>
        </div>
      </section>

      <ClientWall />

      <PageCTA context="New Project" title="Want in on this?" subtitle="Tell us your idea — we'll bring the cameras." />
    </main>
  );
}
